import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ApiError, api } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert } from '../layout';
import type { Scan } from './scan';

interface InboxDocument { id: number; fromAddress: string; subject: string; receivedAt: string; fileName: string; status: 'new' | 'opened' | 'dismissed'; supplier: string | null; number: string | null; total: number | null; read: boolean; senderVerified: boolean; invoiceId: number | null; invoiceLabel: string | null; entered?: { id: number; label: string } | null }
interface Sender { id: number; email: string; confirmedAt: string | null; confirmSentAt: string | null }
interface Inbox { mailbox: string; address: string; enabled: boolean; accounts: string[]; senders: Sender[]; documents: InboxDocument[]; newCount: number }

/** C1 on the received-invoices page: the company's address and the documents that came by e-mail. */
export function InboxBox({ open: unfolded = false }: { open?: boolean } = {}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const inbox = useQuery({ queryKey: ['inbox'], queryFn: () => api.get<Inbox>('api/v1/inbox'), refetchInterval: 60_000 });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['inbox'] });
  const open = useMutation({
    mutationFn: (id: number) => api.post<{ scan: Scan }>(`api/v1/inbox/${id}/open`),
    onSuccess: async (r) => { queryClient.setQueryData(['scan', r.scan.id], r.scan); await refresh(); await navigate({ to: '/incoming/new', search: { scan: r.scan.id } }); },
  });
  const dismiss = useMutation({ mutationFn: (id: number) => api.post(`api/v1/inbox/${id}/dismiss`), onSuccess: refresh });
  // To the accountant (Buhalterija → Buhalteriui), e.g. an invoice in someone else's name.
  const toAccountant = useMutation({ mutationFn: (id: number) => api.post(`api/v1/inbox/${id}/accountant`, { on: true }), onSuccess: refresh });
  const [newSender, setNewSender] = useState('');
  const [mailFailed, setMailFailed] = useState(false);
  const addSender = useMutation({ mutationFn: (email: string) => api.post<{ mailSent: boolean }>('api/v1/inbox/senders', { email }), onSuccess: async (r) => { setNewSender(''); setMailFailed(!r.mailSent); await refresh(); } });
  const resend = useMutation({ mutationFn: (id: number) => api.post<{ mailSent: boolean }>(`api/v1/inbox/senders/${id}/resend`), onSuccess: async (r) => { setMailFailed(!r.mailSent); await refresh(); } });
  const removeSender = useMutation({ mutationFn: (id: number) => api.delete(`api/v1/inbox/senders/${id}`), onSuccess: refresh });
  const data = inbox.data;
  if (!data) return null;
  const waiting = data.documents.filter((d) => !d.invoiceId);
  const entered = data.documents.filter((d) => d.invoiceId);
  const copy = async () => { await navigator.clipboard?.writeText(data.mailbox).catch(() => undefined); setCopied(true); window.setTimeout(() => setCopied(false), 1500); };
  // Reading a document (by AI when needed) takes a while: say so while it runs.
  const reading = open.isPending;
  const error = open.error ?? dismiss.error ?? toAccountant.error ?? addSender.error ?? resend.error ?? removeSender.error;
  const code = error instanceof ApiError ? error.code : '';
  return (
    // Folded when the page opens; the count of new documents shows on the folded bar.
    <details className="panel-card inbox-box fold" open={unfolded}>
      <summary className="card-head">
        <div><p className="eyebrow">{t('Gauta el. paštu')}{data.newCount ? ` · ${t('naujų: {n}', { n: data.newCount })}` : ''}</p><h2>{t('Sąskaitos el. paštu')}</h2></div>
      </summary>
      <p className="muted">{t('Siųskite arba persiųskite tiekėjų sąskaitas šiuo adresu iš žemiau nurodytų savo adresų — jos atsiras čia jau nuskaitytos. Patikrinkite ir išsaugokite.')}</p>
      <div className="inbox-address">
        <code>{data.mailbox}</code>
        <button type="button" className="button button--ghost button--small" onClick={() => void copy()}>{copied ? t('Nukopijuota') : t('Kopijuoti')}</button>
      </div>
      {!data.enabled ? <p className="muted inbox-off">{t('Priėmimas el. paštu dar įjungiamas.')}</p> : null}
      {reading ? <p className="inbox-reading" role="status"><span className="spinner" aria-hidden="true" />{t('Dokumentas skaitomas — tai gali užtrukti iki 30 sekundžių…')}</p> : null}
      {error ? <Alert kind="error">{code === 'sender_exists' ? t('Šis adresas jau pridėtas.') : code === 'sender_is_account' ? t('Paskyros adresas jau įtrauktas automatiškai.') : errorText(t, error)}</Alert> : null}
      {mailFailed ? <Alert kind="error">{t('Patvirtinimo laiško išsiųsti nepavyko. Bandykite vėliau arba parašykite mums.')}</Alert> : null}
      <details className="inbox-senders" open={!data.senders.length}>
        <summary>{t('Adresai, iš kurių siunčiate ({n})', { n: data.accounts.length + data.senders.length })}</summary>
        <ul>
          {data.accounts.map((a) => <li key={a}><span>{a}</span><small className="inbox-ok">{t('paskyros adresas · automatiškai')}</small></li>)}
          {data.senders.map((s) => (
            <li key={s.id}>
              <span>{s.email}</span>
              {s.confirmedAt ? <small className="inbox-ok">{t('patvirtintas')}</small> : <>
                <small className="inbox-wait">{t('laukia patvirtinimo — nuoroda išsiųsta šiuo adresu')}</small>
                <button type="button" className="text-link" disabled={resend.isPending} onClick={() => resend.mutate(s.id)}>{t('Siųsti dar kartą')}</button>
              </>}
              <button type="button" className="delete-x" aria-label={t('Pašalinti')} title={t('Pašalinti')} onClick={() => removeSender.mutate(s.id)}>×</button>
            </li>
          ))}
        </ul>
        <form className="inbox-add" onSubmit={(e) => { e.preventDefault(); if (newSender.trim()) addSender.mutate(newSender.trim()); }}>
          <input type="email" value={newSender} onChange={(e) => setNewSender(e.target.value)} placeholder="buhalterija@jusu-imone.lt" aria-label={t('Naujas adresas')} required />
          <button className="button button--ghost button--small" disabled={addSender.isPending}>+ {t('Pridėti adresą')}</button>
        </form>
      </details>
      {waiting.length ? (
        <div className="inbox-list">
          {waiting.map((d) => (
            <article key={d.id} className={`inbox-item${d.status === 'new' ? ' is-new' : ''}`}>
              <div>
                <strong>{d.supplier || d.fileName}</strong>
                <small>{[d.number, d.total !== null ? money(d.total) : null, d.read ? null : t('nenuskaityta')].filter(Boolean).join(' · ')}</small>
                <small>{d.supplier ? `${d.fileName} · ` : ''}{d.fromAddress} · {new Date(d.receivedAt).toLocaleString()}{d.subject ? ` · ${d.subject}` : ''}</small>
                {d.entered ? <small className="inbox-unverified">{t('Ši sąskaita jau įvesta ({label}) – dokumentą galite atmesti.', { label: d.entered.label })} <Link to="/incoming/$id" params={{ id: String(d.entered.id) }}>{t('Atidaryti')}</Link></small> : null}
                {!d.senderVerified ? <small className="inbox-unverified" title={t('Laiškas atėjo iš jūsų adreso, bet jo siuntimo patvirtinti nepavyko (nėra DKIM parašo ar SPF). Jei siuntėte patys — viskas gerai.')}>⚠ {t('Siuntėjas nepatvirtintas — patikrinkite, ar tikrai siuntėte jūs')}</small> : null}
              </div>
              <div className="row-actions">
                <Link to="/view" search={{ type: 'inbox', id: d.id, title: d.fileName }}>{t('Peržiūrėti')}</Link>
                <button type="button" className="button button--primary button--small" disabled={open.isPending} onClick={() => { if (d.senderVerified || confirm(t('Siuntėjas nepatvirtintas. Ar tikrai šį dokumentą siuntėte jūs (arba jūsų įmonė)?'))) open.mutate(d.id); }}>
                  {open.isPending && open.variables === d.id ? <><span className="spinner" aria-hidden="true" />{t('Skaitoma…')}</> : t('Įvesti')}</button>
                <button type="button" className="text-link" disabled={toAccountant.isPending} title={t('Perduoti buhalteriui sutvarkyti')} onClick={() => toAccountant.mutate(d.id)}>{t('Buhalteriui')}</button>
                <button type="button" className="delete-x" aria-label={t('Atmesti')} title={t('Atmesti')} onClick={() => dismiss.mutate(d.id)}>×</button>
              </div>
            </article>
          ))}
        </div>
      ) : data.documents.length ? <p className="muted">{t('Naujų dokumentų nėra.')}</p> : null}
      {entered.length ? (
        // Already saved as received invoices: out of the way, folded.
        <details className="inbox-entered">
          <summary>{t('Įvesta ({n})', { n: entered.length })}</summary>
          <div className="inbox-list">
            {entered.map((d) => (
              <article key={d.id} className="inbox-item is-entered">
                <div>
                  <strong>{d.supplier || d.fileName}</strong>
                  <small>{d.fromAddress} · {new Date(d.receivedAt).toLocaleString()}</small>
                </div>
                <div className="row-actions">
                  <Link to="/view" search={{ type: 'inbox', id: d.id, title: d.fileName }}>{t('Peržiūrėti')}</Link>
                  <Link to="/incoming/$id" params={{ id: String(d.invoiceId) }}>{t('Sąskaita')} {d.invoiceLabel}</Link>
                </div>
              </article>
            ))}
          </div>
        </details>
      ) : null}
    </details>
  );
}
